<template>
    <LoadingPanel
        :is-pending="isPending"
        :is-error="isError"
    >
        <div
            v-if="data"
            class="actions-section"
        >
            <ColorBadge
                v-if="reportStatus"
                :color="reportStatus.color"
                :text="t(reportStatus.labelKey)"
            />
            <ToggleControl
                v-if="isExcludeToggleVisible"
                :model-value="data.isExcluded ?? false"
                :disabled="!canToggleExclude"
                :label="
                    data.isExcluded
                        ? t('reports.detailsPage.excludedToggle.reinclude')
                        : t('reports.detailsPage.excludedToggle.exclude')
                "
                @update:model-value="onToggleExcludeChange"
            />
        </div>
    </LoadingPanel>
</template>

<script setup lang="ts">
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { computed, toRefs } from 'vue'
import { useReportGetQuery } from '@/api/queries/reports/useReportGetQuery'
import ColorBadge from '@/components/badge/ColorBadge.vue'
import { StatusTypes } from '@/configuration/status/condition-status'
import { useI18n } from 'vue-i18n'
import ToggleControl from '@/components/toggle/ToggleControl.vue'
import { useGlobalConfirmationModal } from '@/composables/useGlobalConfirmationModal'
import { useReportSetExclusionMutation } from '@/api/mutations/reports/useReportSetExclusionMutation'
import { AxiosError } from 'axios'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { ReportType } from '@/configuration/report/report-types'
import { usePermissions } from '@/composables/usePermissions'
import { ComponentType } from '@/configuration/component/component-types'

const reportTypesWithoutExcludeToggle = [ReportType.VisualInspection]

const props = defineProps<{
    reportNumber: string
    reportType: number
    componentSid: string
    componentType: ComponentType
}>()

const { reportNumber, reportType, componentSid, componentType } = toRefs(props)

const { t } = useI18n()
const toasts = usePebbleToasts()
const { userPermissions } = usePermissions()
const triggerConfirmationModal = useGlobalConfirmationModal()

const { data, isPending, isError } = useReportGetQuery(reportNumber, reportType, componentSid)
const { mutateAsync } = useReportSetExclusionMutation()

const isExcludeToggleVisible = computed(() => {
    return !reportTypesWithoutExcludeToggle.includes(reportType.value)
})

const hasPermissionCanExcludeReport = computed(() => {
    if (componentType.value === ComponentType.Transformer) {
        return userPermissions.value.canExcludeTransformerReports
    } 
    
    if (componentType.value === ComponentType.Generator) {
        return userPermissions.value.canExcludeGeneratorReports
    }

    return false
})

const canToggleExclude = computed(() => {
    if (!data.value) {
        return false
    }

    if (!hasPermissionCanExcludeReport.value) {
        return false
    }

    return data.value.isExcluded || data.value.canBeExcluded
})

const reportStatus = computed(() => {
    if (!data) {
        return
    }

    return StatusTypes.find((e) => e.value === data.value?.status)
})

const onToggleExcludeChange = (value: boolean) => {
    triggerConfirmationModal({
        headline: value
            ? t('reports.detailsPage.excludeModal.exclude.headline')
            : t('reports.detailsPage.excludeModal.include.headline'),
        content: value
            ? t('reports.detailsPage.excludeModal.exclude.content')
            : t('reports.detailsPage.excludeModal.include.content'),
        confirmButtonText: t('reports.detailsPage.excludeModal.confirmButtonText'),
        onConfirm: async () => {
            try {
                await mutateAsync({
                    analyticalNumber: reportNumber.value,
                    reportType: reportType.value,
                    isExcluded: value,
                })
            } catch (err) {
                if (err instanceof AxiosError) {
                    toasts?.error({
                        message: t('common.errors.unknown'),
                        duration: 3000,
                    })
                }
            }
        },
    })
}
</script>

<style scoped lang="scss">
.actions-section {
    display: flex;
    align-items: center;
    gap: $spacing-s;
}
</style>
